import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, PlusCircle } from "lucide-react";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import useLocalState from "../lib/useLocalState";

const OVERLINE_CLS = "text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const LABEL_CLS = "block text-xs font-accent uppercase tracking-[0.14em] text-ink-muted mb-1.5";
const INPUT_CLS =
  "w-full bg-white border border-rule px-3 py-2.5 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:border-ink transition-colors";

const BLANK = {
  date: "",
  type: "",
  who: "",
  decided: "",
  youAgreed: "",
  theyAgreed: "",
  disagreed: "",
  noted: "",
  followup: "",
};

const MEETING_TYPES = [
  "Child protection conference",
  "Core group",
  "Child in need meeting",
  "PLO / pre-proceedings meeting",
  "Home visit",
  "Other meeting",
];

function toUkDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function composeWhat(f) {
  const parts = [];
  if (f.decided) parts.push(`Decided: ${f.decided.trim()}`);
  if (f.youAgreed) parts.push(`I agreed to: ${f.youAgreed.trim()}`);
  if (f.theyAgreed) parts.push(`They agreed to: ${f.theyAgreed.trim()}`);
  if (f.disagreed) {
    const noted = f.noted === "yes" ? "noted in the meeting" : f.noted === "no" ? "NOT noted — follow up in writing" : "";
    parts.push(`I disagreed with: ${f.disagreed.trim()}${noted ? ` (${noted})` : ""}`);
  }
  return parts.join(" — ");
}

function appendToChronology(row) {
  const KEY = "yana:chron:rows:v1";
  let rows = [];
  try {
    rows = JSON.parse(window.localStorage.getItem(KEY)) || [];
  } catch {
    rows = [];
  }
  const isBlank = (r) => !(r.date || r.who || r.kind || r.what || r.followup);
  while (rows.length && isBlank(rows[rows.length - 1])) rows.pop();
  rows.push(row);
  window.localStorage.setItem(KEY, JSON.stringify(rows));
}

export default function MeetingDebriefPage() {
  const [form, setForm, clearForm] = useLocalState("yana:debrief:v1", BLANK);
  const [added, setAdded] = useState(false);
  const update = (k) => (e) => {
    setAdded(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const hasContent = !!(form.decided || form.youAgreed || form.theyAgreed || form.disagreed);

  const addToChronology = () => {
    appendToChronology({
      id: Math.random().toString(36).slice(2, 10),
      date: toUkDate(form.date),
      who: form.who.trim(),
      kind: form.type || "Meeting",
      what: composeWhat(form),
      followup: form.followup ? `by ${toUkDate(form.followup)}` : "",
    });
    clearForm();
    setAdded(true);
  };

  return (
    <div data-testid="debrief-page">
      <PageMeta
        title="Meeting debrief — get it into your chronology while it's fresh"
        description="A guided after-meeting form: who was there, what was decided, what each side agreed to, and what you disagreed with — added straight into your chronology with one click."
        path="/understanding/meeting-prep/debrief"
      />
      <PageHeader
        overline="Meeting prep · Afterwards"
        title="Write it down while it's fresh."
        lede="The half hour after a meeting is when your memory is strongest — and when nobody feels like writing. Answer six short questions now, and one dated, factual line goes straight into your chronology. Same day beats perfect."
      />

      <article className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
          <Link
            to="/understanding/meeting-prep"
            data-testid="debrief-back-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to the meeting prep checklist
          </Link>
          <LastReviewed path="/understanding/meeting-prep/debrief" />
        </div>

        {added && (
          <div
            data-testid="debrief-success"
            className="mb-8 border border-river/40 bg-river/5 p-5 flex items-start gap-3"
          >
            <CheckCircle2 size={18} strokeWidth={1.75} className="text-river mt-0.5 shrink-0" />
            <div className="text-sm text-ink leading-relaxed">
              <strong>Added to your chronology.</strong> One dated line is now in your record on this
              device.{" "}
              <Link
                to="/understanding/records/chronology"
                data-testid="debrief-view-chronology"
                className="underline text-river hover:text-ember"
              >
                View your chronology
              </Link>
              {" "}— or fill this in again for another meeting.
            </div>
          </div>
        )}

        <div className="border border-rule bg-linen-alt p-6 md:p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className={LABEL_CLS} htmlFor="debrief-date">Date of the meeting</label>
              <input
                id="debrief-date"
                data-testid="debrief-date"
                type="date"
                value={form.date}
                onChange={update("date")}
                className={INPUT_CLS}
              />
            </div>
            <div className="md:col-span-2">
              <label className={LABEL_CLS} htmlFor="debrief-type">What kind of meeting?</label>
              <select
                id="debrief-type"
                data-testid="debrief-type"
                value={form.type}
                onChange={update("type")}
                className={INPUT_CLS}
              >
                <option value="">Choose…</option>
                {MEETING_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLS} htmlFor="debrief-who">Who was there? (names and roles)</label>
            <input
              id="debrief-who"
              data-testid="debrief-who"
              className={INPUT_CLS}
              value={form.who}
              onChange={update("who")}
              placeholder="e.g. J. Smith (social worker), A. Patel (chair), me, my mother"
            />
          </div>

          <div>
            <label className={LABEL_CLS} htmlFor="debrief-decided">What was decided? (exact words where you can)</label>
            <textarea
              id="debrief-decided"
              data-testid="debrief-decided"
              className={INPUT_CLS}
              rows={3}
              value={form.decided}
              onChange={update("decided")}
              placeholder="e.g. plan stays at child protection; next core group 12/07"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={LABEL_CLS} htmlFor="debrief-you-agreed">What did you agree to do, and by when?</label>
              <textarea
                id="debrief-you-agreed"
                data-testid="debrief-you-agreed"
                className={INPUT_CLS}
                rows={3}
                value={form.youAgreed}
                onChange={update("youAgreed")}
                placeholder="e.g. attend parenting course starting 20/07"
              />
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="debrief-they-agreed">What did they agree to do, and by when?</label>
              <textarea
                id="debrief-they-agreed"
                data-testid="debrief-they-agreed"
                className={INPUT_CLS}
                rows={3}
                value={form.theyAgreed}
                onChange={update("theyAgreed")}
                placeholder="e.g. SW to arrange contact venue by Friday; minutes within 10 days"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="md:col-span-2">
              <label className={LABEL_CLS} htmlFor="debrief-disagreed">Anything you disagreed with?</label>
              <textarea
                id="debrief-disagreed"
                data-testid="debrief-disagreed"
                className={INPUT_CLS}
                rows={2}
                value={form.disagreed}
                onChange={update("disagreed")}
                placeholder="leave blank if nothing"
              />
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor="debrief-noted">Was your disagreement noted?</label>
              <select
                id="debrief-noted"
                data-testid="debrief-noted"
                value={form.noted}
                onChange={update("noted")}
                className={INPUT_CLS}
              >
                <option value="">—</option>
                <option value="yes">Yes, in the meeting</option>
                <option value="no">No — follow up in writing</option>
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLS} htmlFor="debrief-followup">Next follow-up date (if any)</label>
            <input
              id="debrief-followup"
              data-testid="debrief-followup"
              type="date"
              value={form.followup}
              onChange={update("followup")}
              className={INPUT_CLS + " w-auto"}
            />
          </div>

          <div className="pt-2 flex items-center justify-between gap-4 flex-wrap">
            <button
              type="button"
              onClick={addToChronology}
              disabled={!hasContent && !form.who}
              data-testid="debrief-add-button"
              className="inline-flex items-center gap-2 bg-ink text-linen px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:bg-river transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <PlusCircle size={15} strokeWidth={1.75} />
              Add to my chronology
            </button>
            <p className="text-[11px] font-accent text-ink-muted leading-relaxed max-w-xs">
              Saved only in this browser, on this device — the same place your chronology lives.
            </p>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-rule bg-linen-alt p-6">
            <div className={OVERLINE_CLS}>If your disagreement wasn&rsquo;t noted</div>
            <p className="mt-3 text-sm text-ink-muted leading-relaxed">
              Send a short, calm email the same day: &ldquo;At today&rsquo;s meeting I said I disagreed
              with X, for these reasons. Please attach this to the minutes.&rdquo; Now it is on the record.
            </p>
          </div>
          <div className="border border-rule bg-linen-alt p-6">
            <div className={OVERLINE_CLS}>When the minutes arrive</div>
            <p className="mt-3 text-sm text-ink-muted leading-relaxed">
              Check them against this note. Correct any errors in writing and keep a copy — your
              same-day record is the strongest evidence of what was actually said.
            </p>
          </div>
        </div>
      </article>
    </div>
  );
}
